import React, { useState } from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { Modal } from "../../../common/Modal";
import { ImageIcon } from "../../../../assets/ui/icons";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { ImageCropModal } from "./ImageCropModal";
import { ASSET_SPECS, recommendationLine } from "./imageAssets";
import { useLogoBanner } from "./useLogoBanner";

/* Section 05 — Logo & Banner. */

const AssetRow = ({ spec, previewUrl, onChange }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h3 className="m-0 text-[13px] font-bold text-[var(--ink)] font-sans">
        {spec.label}
      </h3>
      <Button variant="primary" size="sm" onClick={onChange}>
        {spec.changeLabel}
      </Button>
    </div>

    <div
      /* The frame is the asset's own aspect ratio, so the banner reads as a
         strip and the logo as a square. */
      style={{ aspectRatio: String(spec.ratio) }}
      className={cn(
        "w-full overflow-hidden rounded-[10px] border border-[var(--line)]",
        "bg-[var(--field-inset)]",
        spec.ratio === 4 ? "max-w-[360px]" : "max-w-[108px]"
      )}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={spec.label}
          className="h-full w-full object-cover"
        />
      ) : (
        /* The live screen shows an empty upload box here; this says which
           asset is missing rather than leaving a blank tile. */
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
          <span aria-hidden="true" className="text-[var(--muted-2)]">
            <ImageIcon size={18} />
          </span>
          <span className="text-[10.5px] leading-tight text-[var(--muted)] font-sans">
            No {spec.ratio === 4 ? "banner" : "logo"} yet
          </span>
        </div>
      )}
    </div>

    <p className="m-0 text-[11.5px] text-[var(--muted)] font-sans">
      {recommendationLine(spec)}
    </p>
  </div>
);

export const LogoBannerSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const { previews, setAsset, isLoading, submit } = useLogoBanner({ onSaved: onNext });
  /* Which asset the dialog is open for — null when closed. One piece of state
     for one dialog. */
  const [editing, setEditing] = useState(null);

  const spec = editing ? ASSET_SPECS[editing] : null;

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar
          onBack={onPrev}
          onSkip={onSkip}
          onContinue={submit}
          isLoading={isLoading}
        />
      }
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Add company logo &amp; banner
      </h2>

      <div className="mt-3.5 rounded-[14px] border border-[var(--line)] bg-white px-4 py-5 sm:px-6 sm:py-6">
        <AssetRow
          spec={ASSET_SPECS.banner}
          previewUrl={previews.banner}
          onChange={() => setEditing("banner")}
        />

        <hr className="my-5 border-0 border-t border-[var(--line)]" />

        <AssetRow
          spec={ASSET_SPECS.logo}
          previewUrl={previews.logo}
          onChange={() => setEditing("logo")}
        />
      </div>

      {spec && (
        <Modal modalHandle={() => setEditing(null)} noClose bare>
          <ImageCropModal
            spec={spec}
            currentUrl={previews[spec.key]}
            onClose={() => setEditing(null)}
            onApply={(file) => {
              setAsset(spec.key, file);
              setEditing(null);
            }}
          />
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default LogoBannerSection;
